// Egy értesítés-sor a harang-panelben: típus-ikon + i18n-rendereltszöveg (cím/törzs) + relatív idő.
//
// BIZTONSÁG: a `data`-ból származó szöveg USER-ADAT (tárgy, nevek, részleg, elutasítás-oka) → SIMA SZÖVEGKÉNT
// renderelünk. `dangerouslySetInnerHTML` TILOS ebben a fájlban.
//
// KATTINTHATÓSÁG: csak ha van feloldható mélylink (`refToRoute`). Nem-kattintható sor (törölt meeting,
// ismeretlen kind) `div`-ként renderel → nincs hamis affordancia és nem visz 404-re.
import { useTranslation } from 'react-i18next';

import { type NotificationRow } from '../api';
import { iconForNotificationType } from '../icons';
import { renderNotificationRow } from '../render';

import { Text } from '@/components/ui/text';
import { formatRelativeTime } from '@/lib/format-datetime';
import { cn } from '@/lib/utils';

export interface NotificationItemProps {
  row: NotificationRow;
  /** Van-e mélylink → kattintható sor (a hívó dönti el a `refToRoute` alapján). */
  navigable: boolean;
  /** Sor-aktiválás (kattintás / Enter / Space): olvasottnak jelölés + navigálás. Csak `navigable` esetén hívjuk. */
  onActivate: () => void;
}

export function NotificationItem({
  row,
  navigable,
  onActivate,
}: NotificationItemProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { title, body } = renderNotificationRow(t, row, i18n.language);
  const Icon = iconForNotificationType(row.type);
  const unread = row.readAt === null;

  const content = (
    <>
      {/* Ikon-kapszula: olvasatlanul brand-tónusú (a design-rendszer `brand-soft` tokenje), olvasva semleges. */}
      <span
        className={cn(
          'mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-lg border [&>svg]:size-3.5',
          unread
            ? 'bg-brand-soft text-brand-dark dark:text-brand border-transparent'
            : 'bg-sunken text-muted-foreground',
        )}
        aria-hidden="true"
      >
        <Icon />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-start gap-1.5">
          <Text
            variant="body"
            as="span"
            className={cn('min-w-0 flex-1 text-left', unread && 'font-medium')}
          >
            {title}
          </Text>
          {unread && (
            // A pötty DEKORATÍV (aria-hidden); az „olvasatlan" tényt külön, csak-képernyőolvasós szöveg közli
            // (egy `span`-en az `aria-label` szerep nélkül nem garantáltan felolvasott).
            <>
              <span aria-hidden="true" className="bg-brand mt-1.5 size-1.5 shrink-0 rounded-full" />
              <span className="sr-only">{t('notify.panel.unreadMark')}</span>
            </>
          )}
        </span>
        <Text variant="secondary" as="span" className="text-left">
          {body}
        </Text>
        <Text variant="secondary" as="span" className="text-subtle text-left">
          {formatRelativeTime(row.createdAt, i18n.language)}
        </Text>
      </span>
    </>
  );

  // A hover/aktív felület a LÁTHATÓ DOBOZHOZ illeszkedik: a sor teljes szélességét fedi (a panel `p-1`
  // belső padding-jén belül), lekerekítéssel — nem az ikon-középpontra igazítva.
  const boxClass = 'flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left';

  // AKCIÓ-KÉPESSÉG: a sor akkor is kattintható, ha NINCS mélylinkje, de még OLVASATLAN — ilyenkor a kattintás
  // csak olvasottnak jelöl (a panel nyitva marad). Így a mélylink nélküli sor (törölt meeting) sem szorul
  // a „Mind olvasottnak" gombra. Olvasott + link nélküli sor: statikus.
  const actionable = navigable || unread;
  if (!actionable) {
    return <div className={boxClass}>{content}</div>;
  }
  return (
    <button
      type="button"
      onClick={onActivate}
      className={cn(
        boxClass,
        'hover:bg-accent focus-visible:shadow-ring-focus transition-colors duration-150 focus-visible:outline-none',
      )}
    >
      {content}
    </button>
  );
}
